import { useXNGSelector } from "../../../context/store";
import { selectUser } from "../../../context/slices/userProfileSlice";
import { ROUTES_XLOGS } from "../../../constants/URLs";
import { SidebarItemAnyProps } from "../../../design/types/sidebar_content";
import { isAtLeastAdmin, isAtLeastEmployee } from "../../../utils/msb_role";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TranslateIcon from "@mui/icons-material/Translate";
import ScienceIcon from "@mui/icons-material/Science";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import RuleIcon from "@mui/icons-material/Rule";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import TuneIcon from "@mui/icons-material/Tune";
import KeyIcon from "@mui/icons-material/Key";
import BlockIcon from "@mui/icons-material/Block";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import UploadFileIcon from "@mui/icons-material/UploadFile";

// Sectioned nav for the MSB zone sidebar (XLOGS-286/287/346): Overview on
// top, then collapsible Billing / Client Management / Imports groups, with
// Billing split into Worklists / Reference / Controls / Test. The zone itself opens at
// ≥ MsbEmployee behind MsbAdminPortalActive (MsbProtectedRoute); each group
// then rides its own flag. Imports keeps its SuperAdmin tier on the write
// surfaces inside the page, not on the button.
export default function useMsbSidebarButtons(): SidebarItemAnyProps[] {
  const flags = useXNGSelector((state) => state.featureFlags.flags);
  const user = useXNGSelector(selectUser);

  const buttons: SidebarItemAnyProps[] = [];
  if (!isAtLeastEmployee(user)) return buttons;

  buttons.push({
    icon: <DashboardIcon fontSize="small" />,
    label: "Overview",
    route: ROUTES_XLOGS._msb.index,
  });

  if (flags["BillingPrototypeActive"]) {
    buttons.push({ header: "Billing", icon: <AccountBalanceWalletIcon fontSize="small" /> });
    buttons.push({ subheader: "Worklists" });
    buttons.push({
      icon: <FactCheckIcon fontSize="small" />,
      label: "Bundles",
      route: ROUTES_XLOGS._msb.bundles,
    });
    buttons.push({
      icon: <UploadFileIcon fontSize="small" />,
      label: "EDI Files",
      route: ROUTES_XLOGS._msb.ediFiles,
    });
    buttons.push({
      icon: <ReceiptLongIcon fontSize="small" />,
      label: "Claim Explorer",
      route: ROUTES_XLOGS._msb.claims,
    });
    buttons.push({
      icon: <MoneyOffIcon fontSize="small" />,
      label: "Denials",
      route: ROUTES_XLOGS._msb.denials,
    });
    buttons.push({
      icon: <PaymentsIcon fontSize="small" />,
      label: "Remittances",
      route: ROUTES_XLOGS._msb.remittances,
    });
    buttons.push({
      icon: <RuleIcon fontSize="small" />,
      label: "Intake Reviews",
      route: ROUTES_XLOGS._msb.intakeReviews,
    });
    buttons.push({
      icon: <PublishedWithChangesIcon fontSize="small" />,
      label: "Corrections",
      route: ROUTES_XLOGS._msb.corrections,
    });
    buttons.push({ subheader: "Reference" });
    buttons.push({
      icon: <MenuBookIcon fontSize="small" />,
      label: "Billing Catalog",
      route: ROUTES_XLOGS._msb.billingCatalog,
    });
    buttons.push({
      icon: <AttachMoneyIcon fontSize="small" />,
      label: "Rate Catalog",
      route: ROUTES_XLOGS._msb.billingRates,
    });
    buttons.push({
      icon: <TranslateIcon fontSize="small" />,
      label: "EDI Glossary",
      route: ROUTES_XLOGS._msb.ediGlossary,
    });
    // Billing exclusions (XLOGS-934): every endpoint behind the page is MsbAdmin.
    if (isAtLeastAdmin(user)) {
      buttons.push({ subheader: "Controls" });
      buttons.push({
        icon: <BlockIcon fontSize="small" />,
        label: "Billing Exclusions",
        route: ROUTES_XLOGS._msb.billingExclusions,
      });
    }
    buttons.push({ subheader: "Test" });
    buttons.push({
      icon: <ScienceIcon fontSize="small" />,
      label: "Claim Shape (Test)",
      route: ROUTES_XLOGS._msb.billingTest,
    });
  }

  // Client Management (XLOGS-286): the customer-facing section — districts
  // now, per-client invoicing/config surfaces as they arrive.
  if (flags["DistrictManagementActive"]) {
    buttons.push({ header: "Client Management", icon: <LocationCityIcon fontSize="small" /> });
    buttons.push({
      icon: <AccountBalanceIcon fontSize="small" />,
      label: "Districts",
      route: ROUTES_XLOGS._msb.districts,
    });
  }

  // Configuration (XLOGS-742/743): runtime configuration keys. Rides no flag;
  // the section is role-only (reads ≥ MsbAdmin, writes SuperAdmin) like the
  // admin API routes behind it.
  if (isAtLeastAdmin(user)) {
    buttons.push({ header: "Configuration", icon: <TuneIcon fontSize="small" /> });
    buttons.push({
      icon: <KeyIcon fontSize="small" />,
      label: "Keys",
      route: ROUTES_XLOGS._msb.configuration,
    });
  }

  // Imports section (XLOGS-211): the page moved here from the admin zone.
  if (flags["ImportsActive"]) {
    buttons.push({ header: "Imports", icon: <CloudUploadIcon fontSize="small" /> });
    buttons.push({
      icon: <CloudUploadIcon fontSize="small" />,
      label: "Import Dashboard",
      route: ROUTES_XLOGS._msb.imports,
    });
  }
  return buttons;
}
